import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from "wavesurfer.js/plugins/regions";
import { autorun } from "mobx";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { TimelineStore } from "../../../librettoEditor/store/TimelineStore";
import type { ScriptLayerScene } from "../../../engine/clips/script/script.interfaces";
import type { SpeakerWaveformColorSegment } from "../../../librettoEditor/store/ScriptLayerStore";

interface ScriptWaveformDisplayProps {
    clipId: string;
    peaks: Float32Array | null;
    peaksRevision?: number;
    duration: number; // in seconds
    rangeEnabled: boolean;
    selectedRange: { start: number; end: number } | null;
    waveformColor?: string;
    gain?: number; // Clip-level gain for visual scaling (1.0 = normal)
    scenes?: ScriptLayerScene[]; // For per-scene gain visualization (Phase 4B)
    speakerColorSegments?: SpeakerWaveformColorSegment[]; // Timeline-domain segments for speaker coloring (Phase 5)
    speakerColorSegmentsRevision?: number;
}

const getWaveformScaleY = (gain: number): number => {
    const g = Number.isFinite(gain) ? Math.max(0, gain) : 1;
    const scaled = Math.sqrt(Math.min(4, g));
    return Math.max(0.12, Math.min(2, scaled));
};

/**
 * Script-specific waveform display component.
 *
 * Key differences from WaveformDisplay:
 * - Uses setSelectedScriptLayerRange() instead of setSelectedRange()
 * - Supports per-scene gain visualization (Phase 4B)
 * - Foundation for speaker coloring (Phase 5)
 */
const ScriptWaveformDisplay: React.FC<ScriptWaveformDisplayProps> = ({
    clipId,
    peaks,
    peaksRevision,
    duration,
    rangeEnabled,
    selectedRange,
    waveformColor,
    gain = 1.0,
    scenes,
    speakerColorSegments,
    speakerColorSegmentsRevision,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer | null>(null);
	const regionsPluginRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
	const activeRegionRef = useRef<any>(null);
	const dragSelectionUnsubRef = useRef<(() => void) | null>(null);
	const isReadyRef = useRef(false);
	const isInternalUpdateRef = useRef(false);
    const lodRef = useRef<{ base: Float32Array | null; levels: Float32Array[] }>({ base: null, levels: [] });
    const lastPeaksRevisionRef = useRef<number>(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const speakerGradientTimeoutRef = useRef<number | null>(null);
    const speakerGradientRafRef = useRef<number | null>(null);
    const lastSpeakerPaintSignatureRef = useRef<string>('');
    const applySpeakerGradientIfAnyRef = useRef<() => void>(() => {});
    const scheduleSpeakerGradientApplyRef = useRef<(delayMs?: number) => void>(() => {});
    const applyWaveformVerticalScaleRef = useRef<() => void>(() => {});
    const canvasIdsRef = useRef<WeakMap<HTMLCanvasElement, number>>(new WeakMap());
    const nextCanvasIdRef = useRef(1);

    // Store in refs to avoid stale closures
    const selectedRangeRef = useRef(selectedRange);
    selectedRangeRef.current = selectedRange;
    const rangeEnabledRef = useRef(rangeEnabled);
    rangeEnabledRef.current = rangeEnabled;

    const [timelinePxPerSec, setTimelinePxPerSec] = useState(TimelineStore.pxPerSec);
    const [timelineZoomPercent, setTimelineZoomPercent] = useState(TimelineStore.zoomPercent);
    const visualGain = useMemo(() => {
        let g = Number.isFinite(gain) ? gain : 1;
        // Prefer selected scene gain when editing a scene on this clip.
        if (
            Array.isArray(scenes) &&
            scenes.length > 0 &&
            ApplicationStore.selectedClipId === clipId &&
            Number.isFinite(Number(ApplicationStore.selectedSceneIndex))
        ) {
            const targetIndex = Number(ApplicationStore.selectedSceneIndex);
            const selectedScene = scenes.find((s) => Number((s as any)?.index) === targetIndex);
            const sceneGain = Number((selectedScene as any)?.properties?.gain);
            if (Number.isFinite(sceneGain)) g = sceneGain;
        }
        return g;
    }, [gain, scenes, clipId, ApplicationStore.selectedClipId, ApplicationStore.selectedSceneIndex]);

    useEffect(() => {
        const dispose = autorun(() => {
            setTimelinePxPerSec(TimelineStore.pxPerSec);
            setTimelineZoomPercent(TimelineStore.zoomPercent);
        });
        return () => dispose();
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const update = () => setContainerWidth(el.clientWidth || 0);
        update();

        if (typeof ResizeObserver === 'undefined') return;
        let rafId = 0;
        const obs = new ResizeObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                update();
            });
        });
        obs.observe(el);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            obs.disconnect();
        };
    }, []);

    const applyRegionBorderStyle = (region: any) => {
        const el = region?.element as HTMLElement | undefined;
        if (!el) return;
        // Use inset shadow so we don't affect region sizing/layout.
        el.style.boxShadow = 'inset 0 0 0 1px rgba(0, 0, 0, 0.45)';
        el.style.borderRadius = '2px';
    };

    // Sync selectedRange to visual region
    const syncRegionToSelection = () => {
        if (isInternalUpdateRef.current) return;
        if (!regionsPluginRef.current) return;

        // Remove existing region
        try {
            activeRegionRef.current?.remove?.();
        } catch (e) {
            // Region might already be removed
        }
        activeRegionRef.current = null;

        // Add new region if there's a selection
        const range = selectedRangeRef.current;
        if (range) {
            const { start, end } = range;
            isInternalUpdateRef.current = true;
            try {
                activeRegionRef.current = regionsPluginRef.current.addRegion({
                    id: 'external',
                    start,
                    end,
                    drag: rangeEnabledRef.current,
                    resize: rangeEnabledRef.current,
                    color: 'rgba(144, 213, 255, 0.2)',
                });
                applyRegionBorderStyle(activeRegionRef.current);
            } catch (e) {
                console.log('[ScriptWaveformDisplay] Error adding region:', e);
            }
            setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
        }
    };

    const waveColors = useMemo(() => {
        // Use provided color or default to dark gray
        const defaultColor = 'rgba(0, 0, 26, 0.9)';
        let waveColor = defaultColor;
        let progressColor = defaultColor;

        if (waveformColor) {
            if (waveformColor.startsWith('#')) {
                // Hex color - convert to rgba with opacity
                const hex = waveformColor.slice(1);
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                waveColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
                progressColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
            } else {
                waveColor = waveformColor;
                progressColor = waveformColor;
            }
        }

        return { waveColor, progressColor };
    }, [waveformColor]);

    const clearSpeakerGradientSchedule = useCallback(() => {
        if (speakerGradientTimeoutRef.current !== null) {
            window.clearTimeout(speakerGradientTimeoutRef.current);
            speakerGradientTimeoutRef.current = null;
        }
        if (speakerGradientRafRef.current !== null) {
            window.cancelAnimationFrame(speakerGradientRafRef.current);
            speakerGradientRafRef.current = null;
        }
    }, []);

    const applySpeakerGradientIfAny = useCallback(() => {
        const ws = wavesurferRef.current;
        if (!ws) return;

        const segments = Array.isArray(speakerColorSegments) ? speakerColorSegments : [];
        const dur = Math.max(0, Number.isFinite(duration) ? duration : 0);
        const EPS = 1e-3;
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        const defaultColor = waveColors.waveColor;

        type CanvasTile = {
            canvas: HTMLCanvasElement;
            canvasId: number;
            leftPx: number;
            widthPx: number;
            startSec: number;
            endSec: number;
        };

        const parsePx = (input: string | null | undefined) => {
            const value = Number.parseFloat(input ?? '');
            return Number.isFinite(value) ? value : 0;
        };

        const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

        const wrapper = ws.getWrapper?.() as HTMLElement | null;
        if (!wrapper) return;
        const scrollContainer = wrapper.parentElement as HTMLElement | null;

        const normalizeSegments = (input: SpeakerWaveformColorSegment[]): SpeakerWaveformColorSegment[] => {
            const filtered: SpeakerWaveformColorSegment[] = [];
            for (const s of input) {
                const start = Number((s as any)?.startSec ?? NaN);
                const end = Number((s as any)?.endSec ?? NaN);
                const color = String((s as any)?.color ?? '').trim();
                if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
                if (!color) continue;
                const clampedStart = Math.max(0, Math.min(dur, start));
                const clampedEnd = Math.max(0, Math.min(dur, end));
                if (clampedEnd - clampedStart <= EPS) continue;
                filtered.push({ startSec: clampedStart, endSec: clampedEnd, color });
            }

            filtered.sort((a, b) => a.startSec - b.startSec);

            // Ensure non-overlap (speaker segments should already be non-overlapping; this is defensive).
            const out: SpeakerWaveformColorSegment[] = [];
            for (const s of filtered) {
                const last = out[out.length - 1];
                if (!last) {
                    out.push({ ...s });
                    continue;
                }
                if (s.startSec <= last.endSec + EPS) {
                    if (s.color === last.color) {
                        last.endSec = Math.max(last.endSec, s.endSec);
                    } else {
                        const clippedStart = Math.max(s.startSec, last.endSec);
                        if (s.endSec - clippedStart > EPS) {
                            out.push({ ...s, startSec: clippedStart });
                        }
                    }
                } else {
                    out.push({ ...s });
                }
            }

            return out;
        };

        const buildFilledSegments = (activeSegs: SpeakerWaveformColorSegment[]): SpeakerWaveformColorSegment[] => {
            const filled: SpeakerWaveformColorSegment[] = [];
            const baseColor = defaultColor;

            let cursor = 0;
            for (const seg of activeSegs) {
                if (seg.startSec > cursor + EPS) {
                    filled.push({ startSec: cursor, endSec: seg.startSec, color: baseColor });
                }
                filled.push(seg);
                cursor = Math.max(cursor, seg.endSec);
            }
            if (cursor < dur - EPS) {
                filled.push({ startSec: cursor, endSec: dur, color: baseColor });
            }

            // Merge adjacent same-color segments.
            const merged: SpeakerWaveformColorSegment[] = [];
            for (const seg of filled) {
                const last = merged[merged.length - 1];
                if (last && last.color === seg.color && Math.abs(last.endSec - seg.startSec) <= EPS) {
                    last.endSec = seg.endSec;
                } else {
                    merged.push({ ...seg });
                }
            }
            return merged;
        };

        const collectVisibleTiles = (selector: string): CanvasTile[] => {
            const canvases = Array.from(wrapper.querySelectorAll(selector)) as HTMLCanvasElement[];
            if (!canvases.length || dur <= EPS) return [];

            let maxRightPx = 0;
            const rawTiles = canvases
                .map((canvas) => {
                    const leftPx = parsePx(canvas.style.left) || canvas.offsetLeft || 0;
                    const widthPx = parsePx(canvas.style.width) || canvas.clientWidth || (canvas.width > 0 ? canvas.width / dpr : 0);
                    if (!Number.isFinite(widthPx) || widthPx <= 0) return null;
                    maxRightPx = Math.max(maxRightPx, leftPx + widthPx);
                    return { canvas, leftPx, widthPx };
                })
                .filter((tile): tile is { canvas: HTMLCanvasElement; leftPx: number; widthPx: number } => !!tile);

            if (!rawTiles.length) return [];

            const totalWidthPx = Math.max(
                wrapper.clientWidth || 0,
                scrollContainer?.scrollWidth || 0,
                maxRightPx,
                1,
            );

            const viewportStartPx = scrollContainer ? Math.max(0, scrollContainer.scrollLeft - 200) : 0;
            const viewportEndPx = scrollContainer
                ? scrollContainer.scrollLeft + (scrollContainer.clientWidth || 0) + 200
                : Number.POSITIVE_INFINITY;

            return rawTiles
                .filter((tile) => tile.leftPx + tile.widthPx >= viewportStartPx && tile.leftPx <= viewportEndPx)
                .map((tile) => {
                    const startSec = clamp01(tile.leftPx / totalWidthPx) * dur;
                    const endSec = clamp01((tile.leftPx + tile.widthPx) / totalWidthPx) * dur;
                    let canvasId = canvasIdsRef.current.get(tile.canvas);
                    if (!canvasId) {
                        canvasId = nextCanvasIdRef.current++;
                        canvasIdsRef.current.set(tile.canvas, canvasId);
                    }
                    return {
                        canvas: tile.canvas,
                        canvasId,
                        leftPx: tile.leftPx,
                        widthPx: tile.widthPx,
                        startSec: Math.min(startSec, endSec),
                        endSec: Math.max(startSec, endSec),
                    };
                })
                .sort((a, b) => a.leftPx - b.leftPx);
        };

        const paintTile = (
            tile: CanvasTile,
            filledSegments: SpeakerWaveformColorSegment[],
            startIndex: number,
        ): number => {
            const spanSec = tile.endSec - tile.startSec;
            if (spanSec <= EPS) return startIndex;
            const ctx = tile.canvas.getContext('2d');
            if (!ctx) return startIndex;

            const pixelWidth = tile.canvas.width || Math.max(1, Math.round(tile.widthPx * dpr));
            const pixelHeight = tile.canvas.height || Math.max(1, Math.round((tile.canvas.clientHeight || 1) * dpr));
            const grad = ctx.createLinearGradient(0, 0, pixelWidth, 0);

            let idx = startIndex;
            while (idx < filledSegments.length && filledSegments[idx].endSec <= tile.startSec + EPS) {
                idx += 1;
            }

            let cursorSec = tile.startSec;
            let paintedAny = false;

            while (cursorSec < tile.endSec - EPS) {
                const seg = filledSegments[idx];
                if (!seg) {
                    const startPos = clamp01((cursorSec - tile.startSec) / spanSec);
                    grad.addColorStop(startPos, defaultColor);
                    grad.addColorStop(1, defaultColor);
                    paintedAny = true;
                    break;
                }

                if (seg.startSec > cursorSec + EPS) {
                    const gapEndSec = Math.min(tile.endSec, seg.startSec);
                    const startPos = clamp01((cursorSec - tile.startSec) / spanSec);
                    const endPos = clamp01((gapEndSec - tile.startSec) / spanSec);
                    grad.addColorStop(startPos, defaultColor);
                    grad.addColorStop(endPos, defaultColor);
                    cursorSec = gapEndSec;
                    paintedAny = true;
                    continue;
                }

                const segStartSec = Math.max(cursorSec, seg.startSec);
                const segEndSec = Math.min(tile.endSec, seg.endSec);
                if (segEndSec <= segStartSec + EPS) {
                    idx += 1;
                    continue;
                }

                const startPos = clamp01((segStartSec - tile.startSec) / spanSec);
                const endPos = clamp01((segEndSec - tile.startSec) / spanSec);
                grad.addColorStop(startPos, seg.color);
                grad.addColorStop(endPos, seg.color);
                cursorSec = segEndSec;
                paintedAny = true;

                if (seg.endSec <= cursorSec + EPS) {
                    idx += 1;
                }
            }

            if (!paintedAny) {
                grad.addColorStop(0, defaultColor);
                grad.addColorStop(1, defaultColor);
            }

            ctx.save();
            ctx.globalCompositeOperation = 'source-in';
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, pixelWidth, pixelHeight);
            ctx.restore();

            return idx;
        };

        const active = normalizeSegments(segments);
        const filled = buildFilledSegments(active);
        if (!filled.length) return;

        const waveTiles = collectVisibleTiles('.canvases canvas');
        const progressTiles = collectVisibleTiles('.progress canvas');
        if (!waveTiles.length && !progressTiles.length) return;

        const tileSignature = (tiles: CanvasTile[]) =>
            tiles.map((tile) => `${tile.canvasId}:${Math.round(tile.leftPx)}:${Math.round(tile.widthPx)}`).join('|');

        const paintSignature = [
            speakerColorSegmentsRevision ?? 'none',
            segments.length,
            Math.round(dur * 1000),
            tileSignature(waveTiles),
            tileSignature(progressTiles),
            defaultColor,
        ].join('::');
        if (paintSignature === lastSpeakerPaintSignatureRef.current) return;

        const PAINT_BUDGET_MS = 12;
        const startedAt = performance.now();

        let segmentIndex = 0;
        for (const tile of waveTiles) {
            if (performance.now() - startedAt > PAINT_BUDGET_MS) {
                lastSpeakerPaintSignatureRef.current = '';
                scheduleSpeakerGradientApplyRef.current(220);
                return;
            }
            segmentIndex = paintTile(tile, filled, segmentIndex);
        }

        segmentIndex = 0;
        for (const tile of progressTiles) {
            if (performance.now() - startedAt > PAINT_BUDGET_MS) {
                lastSpeakerPaintSignatureRef.current = '';
                scheduleSpeakerGradientApplyRef.current(220);
                return;
            }
            segmentIndex = paintTile(tile, filled, segmentIndex);
        }

        lastSpeakerPaintSignatureRef.current = paintSignature;
    }, [
        duration,
        speakerColorSegments,
        speakerColorSegmentsRevision,
        waveColors.waveColor,
    ]);
    applySpeakerGradientIfAnyRef.current = applySpeakerGradientIfAny;

    const scheduleSpeakerGradientApply = useCallback((delayMs = 120) => {
        clearSpeakerGradientSchedule();
        const run = () => {
            speakerGradientRafRef.current = window.requestAnimationFrame(() => {
                speakerGradientRafRef.current = null;
                applySpeakerGradientIfAnyRef.current();
            });
        };
        if (delayMs <= 0) {
            run();
            return;
        }
        speakerGradientTimeoutRef.current = window.setTimeout(() => {
            speakerGradientTimeoutRef.current = null;
            run();
        }, delayMs);
    }, [clearSpeakerGradientSchedule]);
    scheduleSpeakerGradientApplyRef.current = scheduleSpeakerGradientApply;

    useEffect(() => {
        lastSpeakerPaintSignatureRef.current = '';
        scheduleSpeakerGradientApply(60);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        speakerColorSegmentsRevision,
        duration,
        containerWidth,
        waveColors.waveColor,
        waveColors.progressColor,
    ]);

    useEffect(() => {
        return () => {
            clearSpeakerGradientSchedule();
        };
    }, [clearSpeakerGradientSchedule]);

    const downsampleBy2MaxAbsSigned = (input: Float32Array): Float32Array => {
        const outLen = Math.max(1, Math.ceil(input.length / 2));
        const out = new Float32Array(outLen);
        for (let i = 0; i < outLen; i++) {
            const a = input[i * 2] ?? 0;
            const b = input[i * 2 + 1] ?? a;
            out[i] = Math.abs(a) >= Math.abs(b) ? a : b;
        }
        return out;
    };

    const pickLodPeaks = (basePeaks: Float32Array, durationSec: number, pxPerSec: number): Float32Array => {
        if (!Number.isFinite(durationSec) || durationSec <= 0) return basePeaks;
        if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return basePeaks;

        // Aim for a bounded peaks-per-pixel ratio so zoomed-out waveforms don't become a solid block.
        const DESIRED_PEAKS_PER_PX = 1.0;
        const basePeaksPerSec = basePeaks.length / durationSec;
        const desiredPeaksPerSec = Math.min(basePeaksPerSec, pxPerSec * DESIRED_PEAKS_PER_PX);
        const desiredLen = Math.max(1, Math.ceil(durationSec * desiredPeaksPerSec));

        // Refresh LOD cache if base peaks changed.
        if (lodRef.current.base !== basePeaks) {
            lodRef.current.base = basePeaks;
            lodRef.current.levels = [basePeaks];
        }

        let idx = 0;
        while (lodRef.current.levels[idx].length > desiredLen && lodRef.current.levels[idx].length > 1) {
            if (idx + 1 >= lodRef.current.levels.length) {
                lodRef.current.levels.push(downsampleBy2MaxAbsSigned(lodRef.current.levels[idx]));
            }
            idx++;
        }

        return lodRef.current.levels[idx];
    };

    const computeBarHeight = (zoomPercent: number): number => {
        const t = Math.max(0, Math.min(1, (zoomPercent - 50) / 950));
        // Smaller barHeight when zoomed out improves readability for long clips.
        // At 50% zoom-out we still want a clearly visible waveform, so keep this fairly tall.
        const base = 0.5 + (0.9 - 0.5) * Math.sqrt(t);
        const minVisible = 0.18;
        return Math.max(minVisible, base);
    };

    const applyWaveformVerticalScale = useCallback(() => {
        const root = containerRef.current;
        if (!root) return;
        const scaleY = getWaveformScaleY(visualGain);
        const canvases = root.querySelectorAll('.canvases canvas, .progress canvas, canvas');
        canvases.forEach((canvas) => {
            const el = canvas as HTMLCanvasElement;
            el.style.transformOrigin = '50% 50%';
            el.style.transform = `scaleY(${scaleY})`;
            el.style.willChange = 'transform';
        });
    }, [visualGain]);
    applyWaveformVerticalScaleRef.current = applyWaveformVerticalScale;

		useEffect(() => {
		if (!containerRef.current) return;

		// Clean up any drag-selection handler from a previous instance.
		try {
			dragSelectionUnsubRef.current?.();
		} catch {}
		dragSelectionUnsubRef.current = null;

		// Clean up existing instance
		wavesurferRef.current?.destroy();
        isReadyRef.current = false;
        regionsPluginRef.current = null;
        activeRegionRef.current = null;

        // @ts-ignore
	        const ws = WaveSurfer.create({
			container: containerRef.current,
			waveColor: waveColors.waveColor,
			progressColor: waveColors.progressColor,
			cursorWidth: 0,
			interact: false,
			height: "auto",
			normalize: false,
			barWidth: 0.0,
			barGap: 0,
            barRadius: 0,
	            duration: Math.max(0, Number.isFinite(duration) ? duration : 0),
	            barHeight: computeBarHeight(timelineZoomPercent),
	            mediaControls: false,
	        });

        wavesurferRef.current = ws;
        regionsPluginRef.current = ws.registerPlugin(RegionsPlugin.create());

        // Always attach region handlers so toggling the range tool doesn't require a full WaveSurfer re-init.
        regionsPluginRef.current.on('region-created', (region: any) => {
            // Clear any existing regions before creating a new one
            try {
                if (activeRegionRef.current && activeRegionRef.current !== region) {
                    activeRegionRef.current.remove();
                }
            } catch (e) {
                console.log("Error calling remove on active region");
            }
            activeRegionRef.current = region;
            applyRegionBorderStyle(region);

            // Only update store if this was a USER-created region (drag selection)
            // KEY DIFFERENCE: Use setSelectedScriptLayerRange for script clips
            if (!isInternalUpdateRef.current) {
                isInternalUpdateRef.current = true;
                if (ApplicationStore.selectedClipId !== clipId) {
                    ApplicationStore.setSelectedClipId(clipId);
                }
                ApplicationStore.setSelectedScriptLayerRange({ start: region.start, end: region.end });
                setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
            }
        });

        regionsPluginRef.current.on('region-updated', (region: any) => {
            applyRegionBorderStyle(region);
            // KEY DIFFERENCE: Use setSelectedScriptLayerRange for script clips
            if (!isInternalUpdateRef.current) {
                isInternalUpdateRef.current = true;
                if (ApplicationStore.selectedClipId !== clipId) {
                    ApplicationStore.setSelectedClipId(clipId);
                }
                ApplicationStore.setSelectedScriptLayerRange({ start: region.start, end: region.end });
                setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
            }
        });

	        ws.on('ready', () => {
	            isReadyRef.current = true;
	            // Sync any existing selection now that we're ready
	            syncRegionToSelection();
                applyWaveformVerticalScaleRef.current();
	            lastSpeakerPaintSignatureRef.current = '';
	            scheduleSpeakerGradientApply(0);
	        });

	        ws.on('redraw', () => {
	            applyWaveformVerticalScaleRef.current();
	            lastSpeakerPaintSignatureRef.current = '';
	            scheduleSpeakerGradientApply(40);
	        });

	        ws.on('redrawcomplete', () => {
	            applyWaveformVerticalScaleRef.current();
	            lastSpeakerPaintSignatureRef.current = '';
	            scheduleSpeakerGradientApply(0);
	        });

        ws.on('scroll', () => {
            lastSpeakerPaintSignatureRef.current = '';
            scheduleSpeakerGradientApply(50);
        });

		return () => {
			// KEY DIFFERENCE: Clear script layer range instead of regular range
			ApplicationStore.setSelectedScriptLayerRange(null);
			try {
				dragSelectionUnsubRef.current?.();
			} catch {}
			dragSelectionUnsubRef.current = null;
            clearSpeakerGradientSchedule();
			ws.destroy();
			isReadyRef.current = false;
		};
		}, [clipId, waveColors.waveColor, waveColors.progressColor, clearSpeakerGradientSchedule, scheduleSpeakerGradientApply]); // init/destroy only when identity/colors change

    // Update waveform peaks/LOD + barHeight when data or zoom changes (without destroying the instance).
    useEffect(() => {
        const ws = wavesurferRef.current;
        if (!ws) return;
        if (!peaks) return;

        // Progressive waveform compute updates mutate the same Float32Array in-place.
        // Use an external revision to know when to rebuild LOD levels + refresh the WaveSurfer render.
        if (typeof peaksRevision === 'number' && peaksRevision !== lastPeaksRevisionRef.current) {
            lastPeaksRevisionRef.current = peaksRevision;
            lodRef.current.base = null;
            lodRef.current.levels = [];
        }

        const lodPeaks = pickLodPeaks(peaks, duration, timelinePxPerSec);

        try {
            ws.setOptions({
                barHeight: computeBarHeight(timelineZoomPercent),
            } as any);
        } catch {}

        try {
            ws.load('', [lodPeaks], duration);
        } catch (e) {
            console.log('[ScriptWaveformDisplay] Error updating waveform peaks:', e);
        }

        applyWaveformVerticalScaleRef.current();

        // Re-apply speaker gradients after waveform renders new canvases.
        lastSpeakerPaintSignatureRef.current = '';
        scheduleSpeakerGradientApply(80);
    }, [peaks, peaksRevision, duration, timelinePxPerSec, timelineZoomPercent]); // NOTE: selectedRange/rangeEnabled are NOT dependencies

    // Gain visualization: cheap canvas transform only (no peak recompute/reload).
    useEffect(() => {
        applyWaveformVerticalScale();
        const id = window.requestAnimationFrame(() => applyWaveformVerticalScale());
        return () => window.cancelAnimationFrame(id);
    }, [applyWaveformVerticalScale, peaksRevision, duration, containerWidth, timelineZoomPercent]);

    // Separate effect: Sync external selectedRange changes to the visual region
    useEffect(() => {
        if (isReadyRef.current) {
            syncRegionToSelection();
        }
    }, [selectedRange, rangeEnabled]);

	useEffect(() => {
		const plugin = regionsPluginRef.current;
		const ws = wavesurferRef.current;
		if (!plugin || !ws) return;

		ws.setOptions({ interact: rangeEnabled });

		if (rangeEnabled) {
			if (!dragSelectionUnsubRef.current) {
				dragSelectionUnsubRef.current = plugin.enableDragSelection({
					color: 'rgba(144, 213, 255, 0.3)',
					id: "draggable",
				});
			}
		} else {
			try {
				dragSelectionUnsubRef.current?.();
			} catch {}
			dragSelectionUnsubRef.current = null;

			try {
				if (activeRegionRef.current) {
					activeRegionRef.current.remove();
					activeRegionRef.current = null;
                }
            } catch (e) {
                console.log("Exception removing active region")
            }
            // KEY DIFFERENCE: Clear script layer range
            ApplicationStore.setSelectedScriptLayerRange(null);
        }
    }, [rangeEnabled]);

    // Click-away handler
    useEffect(() => {
        if (!rangeEnabled) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Check if the click is on the context menu
                const contextMenu = document.querySelector('.context-menu__content');
                if (contextMenu && contextMenu.contains(event.target as Node)) {
                    return; // Don't clear selection if clicking on context menu
                }

                // Clicked outside the waveform and not on context menu
                try {
                    if (activeRegionRef.current) {
                        activeRegionRef.current.remove();
                        activeRegionRef.current = null;
                    }
                } catch (e) {
                    console.log("Exception removing active region")
                }

                // KEY DIFFERENCE: Clear script layer range
                ApplicationStore.setSelectedScriptLayerRange(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rangeEnabled]);

    return (
        <div
            ref={containerRef}
            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, pointerEvents: rangeEnabled ? 'auto' : 'none' }}
        />
    );
};

export default ScriptWaveformDisplay;
